import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, NavLink } from 'react-router-dom';
import LanguageToggle from './common/LanguageToggle';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';

const Header = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [signingOut, setSigningOut] = useState(false);
  const [isAccountMenuOpen, setIsAccountMenuOpen] = useState(false);
  const { t } = useLanguage();
  const { user, role, signOut } = useAuth();
  const accountMenuRef = useRef(null);

  const isAuthenticated = !!user && role !== 'guest';
  const displayEmail =
    user?.user_metadata?.personal_email ||
    user?.email ||
    t('nav.account', 'Account');

  const toggleMenu = useCallback(() => {
    setIsMobileMenuOpen((prev) => {
      const next = !prev;
      document.body.style.overflow = next ? 'hidden' : '';
      return next;
    });
  }, []);

  const closeMenu = useCallback(() => {
    setIsMobileMenuOpen(false);
    document.body.style.overflow = '';
  }, []);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768 && isMobileMenuOpen) {
        closeMenu();
      }
    };

    const handleEscKey = (event) => {
      if (event.key === 'Escape' && isMobileMenuOpen) {
        closeMenu();
      }
    };

    window.addEventListener('resize', handleResize);
    document.addEventListener('keydown', handleEscKey);

    return () => {
      window.removeEventListener('resize', handleResize);
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [closeMenu, isMobileMenuOpen]);

  const handleSignOut = useCallback(async () => {
    setSigningOut(true);
    try {
      await signOut();
    } catch (err) {
      console.error('Sign out failed', err);
    } finally {
      setSigningOut(false);
    }
  }, [signOut]);

  const handleMobileSignOut = useCallback(async () => {
    await handleSignOut();
    closeMenu();
  }, [handleSignOut, closeMenu]);

  const toggleAccountMenu = useCallback(() => {
    setIsAccountMenuOpen((prev) => !prev);
  }, []);

  const closeAccountMenu = useCallback(() => {
    setIsAccountMenuOpen(false);
  }, []);

  useEffect(() => {
    if (!isAccountMenuOpen) return;
    const handleClickOutside = (event) => {
      if (
        accountMenuRef.current &&
        !accountMenuRef.current.contains(event.target)
      ) {
        setIsAccountMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isAccountMenuOpen]);

  const navLinkClasses = ({ isActive }) =>
    `premium-link text-white font-medium transition-colors duration-200 ${
      isActive ? 'text-teal-400' : 'hover:text-teal-300'
    }`;

  return (
    <>
      <header className="sticky top-0 z-50">
        <div className="glass">
          <div className="container mx-auto px-4 sm:px-6">
            <div className="flex justify-between items-center py-3 md:py-4">
              {/* Logo */}
              <div className="h-12 flex-shrink-0 pl-2 md:pl-4">
                <Link to="/">
                  <img
                    src="/img/logo.png"
                    alt="Ravana Industries Logo"
                    className="h-full w-auto object-contain hover:scale-105 transition-transform duration-300"
                    loading="eager"
                  />
                </Link>
              </div>

              {/* Desktop Navigation */}
              <nav className="hidden md:flex flex-grow justify-center items-center gap-8">
                <NavLink to="/" className={navLinkClasses}>{t('nav.home', 'Home')}</NavLink>
                <NavLink to="/services" className={navLinkClasses}>{t('nav.services', 'Services')}</NavLink>
                <NavLink to="/pricing" className={navLinkClasses}>{t('nav.pricing', 'Pricing')}</NavLink>
                <NavLink to="/about" className={navLinkClasses}>{t('nav.about', 'About')}</NavLink>
                <NavLink to="/choice" className={navLinkClasses}>{t('nav.consult', 'Consult')}</NavLink>
                <NavLink to="/contact" className={navLinkClasses}>{t('nav.contact', 'Contact')}</NavLink>
              </nav>

              {/* Right side actions */}
              <div className="hidden md:flex items-center gap-3 md:gap-6 pr-2 md:pr-4">
                <Link
                  to="/portal"
                  className="btn btn-outline text-sm"
                >
                  Client Portal
                </Link>
                {isAuthenticated && (
                  <div className="relative" ref={accountMenuRef}>
                    <button
                      type="button"
                      className="btn btn-outline text-sm inline-flex items-center gap-2"
                      onClick={toggleAccountMenu}
                      aria-expanded={isAccountMenuOpen}
                      aria-haspopup="true"
                    >
                      <span className="inline-block h-8 w-8 rounded-full bg-teal-500 text-white flex items-center justify-center uppercase text-sm font-semibold">
                        {displayEmail?.slice(0, 1)}
                      </span>
                      <span className="max-w-[12rem] truncate text-left">
                        {displayEmail}
                      </span>
                      <svg
                        className={`w-4 h-4 transition-transform ${
                          isAccountMenuOpen ? 'rotate-180' : ''
                        }`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>
                    </button>
                    {isAccountMenuOpen && (
                      <div
                        className="absolute right-0 mt-2 w-64 bg-white text-slate-700 shadow-xl rounded-lg border border-slate-100 overflow-hidden z-50"
                        role="menu"
                      >
                        <div className="px-4 py-3 border-b border-slate-100">
                          <p className="text-xs uppercase tracking-wide text-slate-400">
                            Signed in as
                          </p>
                          <p className="text-sm font-semibold truncate">
                            {displayEmail}
                          </p>
                        </div>
                        <nav className="py-1">
                          <Link
                            to="/admin/profile"
                            className="flex items-center gap-2 px-4 py-2 text-sm hover:bg-slate-50 transition-colors"
                            onClick={closeAccountMenu}
                          >
                            <span className="text-lg" aria-hidden="true">
                              👤
                            </span>
                            Profile & Security
                          </Link>
                        </nav>
                        <button
                          type="button"
                          onClick={() => {
                            closeAccountMenu();
                            handleSignOut();
                          }}
                          className="w-full text-left flex items-center gap-2 px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors border-t border-slate-100"
                          disabled={signingOut}
                        >
                          <span className="text-lg" aria-hidden="true">
                            ⛔
                          </span>
                          {signingOut ? 'Signing out…' : 'Log out'}
                        </button>
                      </div>
                    )}
                  </div>
                )}
                <LanguageToggle variant="tabs" />
              </div>

              {/* Mobile menu button */}
              <button
                className="mobile-menu-button md:hidden text-slate-800 hover:text-teal-600 focus:outline-none focus:ring-2 focus:ring-teal-400 rounded-lg"
                id="menuButton"
                aria-label="Toggle menu"
                aria-expanded={isMobileMenuOpen}
                onClick={toggleMenu}
              >
                {isMobileMenuOpen ? (
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                ) : (
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                )}
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile menu overlay */}
      {isMobileMenuOpen && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 md:hidden animate-fade-in"
          onClick={closeMenu}
          aria-hidden="true"
        />
      )}

      {/* Mobile menu */}
      <div
        className={`mobile-menu fixed top-0 right-0 w-80 max-w-[85vw] h-full glass z-50 md:hidden transform transition-transform duration-300 ease-in-out ${
          isMobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="mobile-menu-title"
      >
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="p-5 border-b border-white/20 flex justify-between items-center flex-shrink-0">
            <div className="h-10">
              <Link to="/" onClick={closeMenu}>
                <img src="/img/logo.png" alt="Ravana Industries Logo" className="h-full" />
              </Link>
            </div>
            <button
              className="p-2 text-white hover:text-teal-300 transition-colors focus:outline-none focus:ring-2 focus:ring-teal-400 rounded-lg"
              onClick={closeMenu}
              aria-label="Close menu"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Navigation Links */}
          <nav className="flex flex-col p-5 gap-1 flex-grow overflow-y-auto">
            <Link
              to="/"
              className="text-white font-medium hover:text-teal-300 py-3 px-4 rounded-lg transition-colors hover:bg-white/10 flex items-center gap-3"
              onClick={closeMenu}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
              </svg>
              <span>{t('nav.home', 'Home')}</span>
            </Link>
            <Link
              to="/services"
              className="text-white font-medium hover:text-teal-300 py-3 px-4 rounded-lg transition-colors hover:bg-white/10 flex items-center gap-3"
              onClick={closeMenu}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
              </svg>
              <span>{t('nav.services', 'Services')}</span>
            </Link>
            <Link
              to="/pricing"
              className="text-white font-medium hover:text-teal-300 py-3 px-4 rounded-lg transition-colors hover:bg-white/10 flex items-center gap-3"
              onClick={closeMenu}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>{t('nav.pricing', 'Pricing')}</span>
            </Link>
            <Link
              to="/about"
              className="text-white font-medium hover:text-teal-300 py-3 px-4 rounded-lg transition-colors hover:bg-white/10 flex items-center gap-3"
              onClick={closeMenu}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>{t('nav.about', 'About')}</span>
            </Link>
            <Link
              to="/choice"
              className="text-white font-medium hover:text-teal-300 py-3 px-4 rounded-lg transition-colors hover:bg-white/10 flex items-center gap-3"
              onClick={closeMenu}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
              <span>{t('nav.consult', 'Consult')}</span>
            </Link>
            <Link
              to="/contact"
              className="text-white font-medium hover:text-teal-300 py-3 px-4 rounded-lg transition-colors hover:bg-white/10 flex items-center gap-3"
              onClick={closeMenu}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              <span>{t('nav.contact', 'Contact')}</span>
            </Link>
          </nav>

          {/* Bottom section */}
          <div className="p-5 border-t border-white/20 flex-shrink-0 space-y-4">
            <LanguageToggle variant="compact" />
            <Link
              to="/portal"
              className="block bg-white/20 backdrop-blur-md text-white px-4 py-3 rounded-lg hover:bg-white/30 transition-all duration-300 text-center border border-white/30 font-medium"
              onClick={closeMenu}
            >
              Client Portal
            </Link>
            {isAuthenticated && (
              <div className="space-y-3">
                <div className="bg-white/10 rounded-lg px-4 py-3 text-white text-sm border border-white/20">
                  <p className="text-xs uppercase tracking-wide text-white/80">
                    Signed in as
                  </p>
                  <p className="font-semibold break-all">{displayEmail}</p>
                </div>
                <Link
                  to="/admin/profile"
                  className="btn btn-outline w-full text-sm text-center"
                  onClick={closeMenu}
                >
                  Profile & Security
                </Link>
                <button
                  type="button"
                  className="btn btn-outline w-full text-sm"
                  onClick={handleMobileSignOut}
                  disabled={signingOut}
                >
                  {signingOut ? 'Signing out…' : 'Log out'}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default Header;
